import {
	Link,
	useNavigate,
	useRouter,
	useSearch,
} from '@tanstack/react-router';
import { CurrencyType, type ShipmentListDTO } from '@uturn/api/v1';
import { GetShipmentsParams } from '@uturn/api/v3';
import { AssignedShipments as AssignedShipmentsEmptyState } from '@uturn/design-tokens/empty-states';
import { ShipmentPage } from '@uturn/portal/modules/shipments/components';
import { CarrierInformationSheet } from '@uturn/portal/modules/shipments/components/shipment-sheet';
import { useShipmentsSearchStore } from '@uturn/portal/store/shipments/shipments-search';
import { formatPrice } from '@uturn/portal/utils';
import { Button, EmptyState } from '@uturn/ui-kit';
import { useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { cleanFilterParams } from '../../components/filters/utils-filter-params';
import { ShipmentGroupDetailContext } from '../../providers/shipment-group-detail';

function AssignedShipmentEmptyState() {
	const { t } = useTranslation();
	return (
		<EmptyState>
			<AssignedShipmentsEmptyState className="md:h-80" />
			<h2 className="text-secondary-700 text-2xl font-medium leading-7">
				{t(
					'pages.shipments.assigned.emptyState.title',
					'Match made in heaven!',
				)}
			</h2>
			<p className="text-secondary-600 text-lg leading-6">
				{t(
					'pages.shipments.assigned.emptyState.description',
					'Here your containers find their trucks. You can manage and monitor progress of the transports.',
				)}
			</p>
			<div>
				<Link to="/shipments/add">
					<Button>{t('pages.shipments.actions.start', 'Get started')}</Button>
				</Link>
			</div>
		</EmptyState>
	);
}

export function AssignedGroupShipments() {
	const { t } = useTranslation();
	const { groupName } = useContext(ShipmentGroupDetailContext);
	const { defaultFilters, setShipmentsSearchFilters } =
		useShipmentsSearchStore();

	const router = useRouter();
	const navigate = useNavigate({ from: '/shipments/groups/$groupId/assigned' });
	const search = useSearch({
		from: '/_protected/shipments/groups/$groupId/assigned',
	});

	const shipmentDetails = (shipment: ShipmentListDTO) => [
		{
			label: t('pages.shipments.details.reference', 'Reference'),
			value: shipment.shipperReference,
			visibleOnMultiSelect: true,
			enableCopyToClipboard: true,
		},
		{
			label: t('pages.shipments.details.containerNumber', 'Container number'),
			value: shipment.containerNumber,
			visibleOnMultiSelect: true,
			enableCopyToClipboard: true,
		},
		{
			label: t('pages.shipments.details.carrierName', 'Carrier name'),
			value: (
				<CarrierInformationSheet
					shipmentNumber={shipment.shipmentNumber}
					carrierName={shipment.carrierOrganizationName ?? '-'}
				/>
			),
			visibleOnMultiSelect: true,
			clickable: true,
		},
		{
			label: t('pages.shipments.details.totalPrice', 'Agreed total price'),
			value: formatPrice(
				shipment.priceIncludingShipmentFee?.quantity ?? 0,
				shipment.priceIncludingShipmentFee?.currencyCode ?? CurrencyType.EUR,
			),
			visibleOnMultiSelect: true,
			hasInfoIcon: true,
		},
	];

	return (
		<>
			<Helmet
				title={t(
					'pages.shipments.groups.assigned.title',
					'{{groupName}} - Assigned shipments',
					{
						groupName,
					},
				).toString()}
			/>
			<ShipmentPage
				enableGroupFilter={false}
				EmptyState={AssignedShipmentEmptyState}
				details={shipmentDetails}
				search={{
					...search,
					page: search.page.toString(),
					size: search.size.toString(),
				}}
				filterOnChange={(filters) => {
					if (filters === null || Object.keys(filters).length === 0) {
						return router.invalidate();
					}

					const newFilters: GetShipmentsParams = {
						...defaultFilters,
						...cleanFilterParams(filters),
					};

					setShipmentsSearchFilters(newFilters);

					navigate({
						search: {
							...newFilters,
							page: Number(newFilters.page),
							size: Number(newFilters.size),
						},
					});
				}}
			/>
		</>
	);
}
